import { api } from '../../lib/api/client';

/**
 * A saved-for-later capture. Everything needed to resume the workflow exactly where the operator left
 * off: demographic answers, captured print images, amputation/unprintable marks, uploaded document
 * keys, and which step they were on.
 *
 * The body lives in S3 rather than DynamoDB because a full 14-position print set base64-encodes to
 * several megabytes; only a small pointer row is kept in the table, with a 30-day TTL that the
 * bucket's `expire-drafts` lifecycle rule mirrors.
 */
export interface DraftPayload {
  captureType: string;
  demo: Record<string, string>;
  prints: Record<string, string>;
  ampCodes: Record<string, 'XX' | 'UP'>;
  /**
   * Advisory NFIQ per position. Optional so drafts written before it existed still load — absent simply
   * means the impressions were never scored, which the card renders as no badge.
   */
  quality?: Record<string, number | null>;
  docKeys: string[];
  step: string;
  /** Schema marker, so a future shape change can migrate or reject old drafts rather than misread them. */
  v: 1;
}

export interface DraftSummary {
  draftId: string;
  captureType: string;
  firstName: string | null;
  lastName: string | null;
  createdOn?: string;
  expiresOn?: string | null;
}

/**
 * Save (or overwrite) a draft. The body is PUT straight to S3 from the browser via a presigned URL —
 * routing multiple megabytes of print data through the API Lambda would risk its 6 MB request limit —
 * and only then is the pointer row written.
 */
export async function saveDraft(payload: DraftPayload, draftId?: string): Promise<string> {
  const presign = await api.post<{ draftId: string; url: string }>(
    '/drafts/presign',
    draftId ? { draftId } : {},
  );
  const put = await fetch(presign.url, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!put.ok) throw new Error(`Could not upload the draft (${put.status})`);
  const saved = await api.post<{ draftId: string }>('/drafts', {
    draftId: presign.draftId,
    captureType: payload.captureType,
    ...(payload.demo['firstName'] ? { firstName: payload.demo['firstName'] } : {}),
    ...(payload.demo['lastName'] ? { lastName: payload.demo['lastName'] } : {}),
  });
  return saved.draftId;
}

export function listDrafts(): Promise<DraftSummary[]> {
  return api.get<DraftSummary[]>('/drafts');
}

/** Load a draft body. The API hands back a presigned GET so the browser reads S3 directly. */
export async function loadDraft(draftId: string): Promise<DraftPayload> {
  const res = await api.get<{ draftId: string; captureType: string; url: string }>(
    `/drafts/${encodeURIComponent(draftId)}`,
  );
  const body = await fetch(res.url);
  if (!body.ok) throw new Error(`Could not read the saved draft (${body.status})`);
  const payload = (await body.json()) as DraftPayload;
  if (payload?.v !== 1) throw new Error('This draft was saved by an older version and cannot be opened.');
  return payload;
}

export function deleteDraft(draftId: string): Promise<unknown> {
  return api.del(`/drafts/${encodeURIComponent(draftId)}`);
}
